import { DataBinning } from "../classificators/dataBinning/index.js";
import { BaseballResultsProcessor, ChartSeries, Series } from "../entities/index.js";
import { PitchReprocessed, ResultData } from "../enums/index.js";
import { buildPitchPlayerFilters, buildPitchScoringFilters, buildPitchTaggingFilters } from "../filters/byContext.js";
import { filterOnlyHavingParameterSet } from "../filters/byParameterName.js";
import { BinToCountConverter, BinValueConverterBase } from '../converters/binValueConverters/index.js';
import { ResultPair } from "../valueObjects/index.js";

class StrikeZoneHeatMapSeriesProvider{

    constructor(dataBinning, customResultsProcessor) {
        if (!(dataBinning instanceof DataBinning)) {
            throw new TypeError('dataBinning must be an instance inheriting from class DataBinning');
        }

        this.resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();

        if (!(this.resultsProcessor instanceof BaseballResultsProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from BaseballResultsProcessor class');
        }

        this.dataBinning = dataBinning;
    }

    extractPitches(resultPairs) {
        return resultPairs.reduce((pitches, pair) => {
            if (pair.hasPitch()) {
                pitches.push(pair.getPitch());
            }

            return pitches;
        }, []);
    }

    buildFiltersArray({
        // player context
        pitcherIds, batterIds,
        // tagging context
        pitchResults, pitchTypes, pitchSet, zones, batterHandedness, pitcherHandedness,
        // scoring context 
        outs, balls, strikes, runnersOn,
    }) {
        let pitchingFilters = buildPitchTaggingFilters(
            { pitchResults, pitchTypes, pitchSet, zones, batterHandedness, pitcherHandedness },
        );

        pitchingFilters = pitchingFilters.concat(buildPitchScoringFilters({
            outs, balls, strikes, runnersOn
        }));
        
        pitchingFilters = pitchingFilters.concat(buildPitchPlayerFilters(pitcherIds, batterIds));
    
        return pitchingFilters.concat(
            filterOnlyHavingParameterSet(ResultData.getDataPath(PitchReprocessed.StrikeZonePositionR.key))
        );
    }

    binToSeriesMapper(bins, sourcesPerBin, binsSourcesMap, binValueConverter) {
        const series = new Series('bins', 2);
        for (let c = 0; c < bins.length; c++) {
            const bin = bins[c];
            series.add(
                binsSourcesMap ? binsSourcesMap[c]: undefined,
                bin.fromWhere(),
                binValueConverter.getBinValue(bin, sourcesPerBin ? sourcesPerBin[c]: [])
            )
        }

        return series;
    };

    matchSourcesToBins(bins, resultPairs, mergedPoints, mergedSourcesMap) {
        const binsSourcesMap = [];
        const sourcesPerBin = [];
    
        // no we need to go over bins
        for (let c = 0; c < bins.length; c++) {
            const binData = bins[c].getValues();

            binsSourcesMap[c] = [];
            sourcesPerBin[c] = [];

            for (let p = 0; p < binData.length; p++) {
                const binPoint = binData[p];
                binsSourcesMap[c][p] = [];
                sourcesPerBin[c][p] = [];
                
                for (let i = 0; i < mergedPoints.length; i++) {
                    const point = mergedPoints[i];

                    if (typeof(point) === 'undefined') {
                        continue;
                    }

                    if (point === binPoint) {
                        const resultId = mergedSourcesMap[i];
                        binsSourcesMap[c][p] = resultId;
                        sourcesPerBin[c][p] = resultPairs.find(r => r[ResultPair.getPitchProperty()][ResultData.Id.key] === resultId);
                        
                        // removing mergedPoint to speed further iterations
                        delete mergedPoints[i];
                        break;
                    }
                }
            }
        }
    
        return { binsSourcesMap, sourcesPerBin };
    }

    getSeriesForHeatMap(
        results, binValueConverter,
        {
            // player context
            batterIds, pitcherIds,
            // tagging context
            pitchResults, pitchTypes, zones, pitcherHandedness, batterHandedness, pitchSet,
            // scoring context
            outs, balls, strikes, runnersOn,
        } = {}
    ) {

        let _binValueConverter = null;

        if (typeof(binValueConverter) === 'undefined') {
            _binValueConverter = new BinToCountConverter();
        }
        else if (!(binValueConverter instanceof BinValueConverterBase)) {
            throw new TypeError('binValueConverter must be an instance inheriting from BinValueConverterBase');
        }
        else {
            _binValueConverter = binValueConverter;
        }
    
        if (!results instanceof Array) {
            throw new TypeError('results must contain array');
        }

        let pitchFilters = this.buildFiltersArray({
            pitcherIds, batterIds,
            pitchResults, pitchTypes, pitchSet, zones, batterHandedness, pitcherHandedness,
            outs, balls, strikes, runnersOn,
        });

        // we will work on result pairs, so we need to include pitch prefix for all
        for (let i = 0; i < pitchFilters.length; i++) {
            const filter = pitchFilters[i];
            filter.parameter = [ResultPair.getPitchProperty()].concat(filter.parameter);
        }
        // and also look for pitch only stuff...
        pitchFilters = filterOnlyHavingParameterSet(ResultPair.getPitchProperty()).concat(pitchFilters);

        // match pitch and hit...
        const matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);

        const pairsWithLocation =  this.resultsProcessor.filterData(matchedResults, pitchFilters);
        const resultsMapped = this.resultsProcessor.mapCollectionProperties(pairsWithLocation, [
            ['pitch', ResultData.Id.key],
            //['pitch', ResultData.HitId.key],
            ['pitch'].concat(ResultData.getDataPath(PitchReprocessed.StrikeZonePositionR.key))
        ]);

        const strikeZoneValues = resultsMapped.map(x => Number.parseInt(x[PitchReprocessed.StrikeZonePositionR.key]));
        const resultIds =  resultsMapped.map(x => x[ResultData.Id.key]);
        const bins = this.dataBinning.
            determineBins(strikeZoneValues)
            // no we need to remove bin nr 10, as there is no zone 10
            .filter(b => b.fromWhere() !== 10)
        ;

        // so now we have bins without reference to the results, we must now re-define resultsMap
        const {binsSourcesMap, sourcesPerBin} = this.matchSourcesToBins(
            bins, pairsWithLocation, strikeZoneValues, resultIds
        );

        // now we need to format back to heat map data
        return {
            series: new ChartSeries(
                [this.binToSeriesMapper(bins, sourcesPerBin, binsSourcesMap, _binValueConverter)]
            ),
            bins
        };
    }
}

export default StrikeZoneHeatMapSeriesProvider;